import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core';

import { FetchHelper } from '../../../globals/FetchHelper';
import { MarketWebContextService } from '../../../globals/MarketWebContextService';
import { MenuSiteDefinition, SiteFooterContentModel } from './pn-marketweb-sitefooter-types';
import { getSocialMediaIconsByType } from './socialMediaIcons';
import { translations } from './translations';
import { HeadingTag } from '../../../globals/HeadingTag';

@Component({
  tag: 'pn-marketweb-sitefooter',
  styleUrl: 'pn-marketweb-sitefooter.scss',
})
export class PnMarketwebSiteheader {
  @Element() hostElement: Element;

  @Prop({ mutable: true, reflect: true }) market: string = 'se';
  @Prop({ mutable: true, reflect: true }) language: string = 'sv';
  @Prop({ mutable: true, reflect: true }) environment: string = null;
  @Prop() endpoint: string = null;

  /** Adjusts the menus URLs */
  @Prop() siteDomainInUrls: boolean = false;

  /** If the component should use cached requests */
  @Prop() cache: boolean = false;

  /** dotcom prop */
  @Prop({ reflect: true }) theme: string = 'default';
  /** dotcom backgroundcolor */
  @Prop({ reflect: true }) backgroundcolor: string = 'darkestblue';

  @Prop() showSwan: boolean = false;
  @Prop() headingLevel: string = "h2";

  @State() siteDefinition: MenuSiteDefinition;
  @State() footerContent: SiteFooterContentModel;

  @State() i18n: any;
  @State() gotData: boolean = false;
  @State() fetchingData: boolean = false;

  fetchHelper = new FetchHelper('sitefooter');
  endpointPath = '/api/footer/footer';

  componentWillLoad() {
    this.setInitialValues().then(() => {
      this.init();
    });
  }

  async setInitialValues(href = window.location.href) {
    const marketWebContextService = new MarketWebContextService(href);
    if (!this.market) {
      const resolvedMarket = await marketWebContextService.getMarket();
      this.market = resolvedMarket;
    }
    if (!this.language) {
      const resolvedMarket = await marketWebContextService.getLanguage();
      this.language = resolvedMarket;
    }
    if (!this.environment) {
      this.environment = await marketWebContextService.getEnvironmentName();
    }
    if (!this.endpoint && this.environment?.indexOf('local') === -1) {
      this.endpoint = await marketWebContextService.getEndpoint(this.environment, this.market);
    }
    if (!this.endpoint) {
      this.endpoint = '';
    }
  }

  async init() {
    this.setTranslations();
    await this.fetchData();
  }

  setTranslations() {
    if (this.language && translations[this.language]) {
      this.i18n = translations[this.language];
    }
  }

  @Watch('language')
  onLanguageChange() {
    this.setTranslations();
    this.fetchData();
  }

  @Watch('market')
  onMarketChange() {
    this.fetchData();
  }

  setFetchUrl(endpointBase: string) {
    switch (this.theme) {
      case 'dotcom':
        return `${endpointBase}${this.endpointPath}?market=${this.market}&language=${this.language}&breakOutSocialMedia=true&extendColumns=true`;
      default:
        return `${endpointBase}${this.endpointPath}?market=${this.market}&language=${this.language}`;
    }
  }

  async fetchData() {
    if (this.endpoint === null || !this.market || !this.language) {
      // console.warn(`One or more values where not specified.\nEndpoint: ${this.endpoint}\nMarket: ${this.market}\nLanguage: ${this.language}`)
      return;
    }

    this.fetchingData = true;
    const endpointBase = this.endpoint.lastIndexOf('/') === this.endpoint.length - 1 ? this.endpoint.substring(0, this.endpoint.length - 1) : this.endpoint;
    const fetchUrl = this.setFetchUrl(endpointBase);
    const data = (await this.fetchHelper.fetchJson(
      fetchUrl,
      {
        mode: 'cors',
      },
      this.cache,
      this.onFetchRefreshed.bind(this),
    )) as any;
    await this.setStateFromData(data);
  }

  onFetchRefreshed(data) {
    this.setStateFromData(data);
  }

  setStateFromData(data: any) {
    if (typeof data !== 'object') {
      console.warn('Data was not valid', data);
    }

    this.siteDefinition = data.sitedefinition;
    this.footerContent = data.footer;

    if (this.siteDomainInUrls) {
      this.adjustLinks();
    }

    this.gotData = true;
    this.fetchingData = true;
  }
  adjustLinks() {
    this.footerContent.bottomLinks.forEach(link => {
      link.linkHref = this.adjustHref(link.linkHref);
    });
    this.footerContent.columns.forEach(col => {
      col.links.forEach(link => {
        link.linkHref = this.adjustHref(link.linkHref);
      });
    });
  }

  adjustHref(href: string) {
    if (!href || href.indexOf('http') === 0 || this.siteDomainInUrls) {
      return href;
    }
    const domainUrl =
      this.siteDefinition.url.lastIndexOf('/') === this.siteDefinition.url.length - 1
        ? this.siteDefinition.url.substring(0, this.siteDefinition.url.length - 1)
        : this.siteDefinition.url;
    href = domainUrl + href;
    return href;
  }

  uniqueId(text: string) {
    return 'footer-' + text.replace(/[^A-Za-z0-9.\\\/]/gim, '');
  }

  renderCols() {
    if (this.gotData && this.footerContent?.columns) {
      return this.footerContent.columns.map(column => {
        return (
          <pn-site-footer-col theme={this.theme}>
            {column.firstLinkAsHeader && column.links && column.links[0] ? (
              <a
                href={column.links[0].linkHref}
                target={column.links[0].openInNewWindow ? '_blank' : null}
                rel={column.links[0].openInNewWindow ? 'noopener nofollower noreferrer' : null}
                title={column.links[0].linkText}
                id={this.uniqueId(column.links[0].linkText)}
              >
                <HeadingTag level={this.headingLevel} cssClass={`${"pn-" + this.headingLevel}`}>
                  {column.links[0].linkText}
                </HeadingTag>
              </a>
            ) : (
              <HeadingTag level={this.headingLevel} cssClass={`${"pn-" + this.headingLevel}`}>
                {column.heading}
              </HeadingTag>
            )}
            {column.links && (!column.body || this.theme === 'tpl') ? (
              <ul {...(column.links && column.links[0] && column.links[0].linkType && { class: 'social-media' })}>
                {column.links.map((link, i) => {
                  if (!link.linkHref || (column.firstLinkAsHeader && i == 0)) {
                    return false;
                  }
                  let linkText = link.linkText;
                  if (link.linkType) {
                    linkText = getSocialMediaIconsByType(link.linkType);
                  }
                  let linkId = this.uniqueId(linkText);
                  return (
                    <li>
                      <a href={link.linkHref} {...(link.openInNewWindow && { target: '_blank' })} title={link.linkText} innerHTML={linkText} id={linkId}>
                        {this.theme === 'tpl' ? <span class="footer-tpl-links">{link.linkText}</span> : null}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {column.body ? <div innerHTML={column.body}></div> : null}
            <slot name="bottomsocialmediadesktop" />
            <slot name="bottomsocialmediamobile" />
          </pn-site-footer-col>
        );
      });
    } else {
      return null;
    }
  }

  renderBottomLinks() {
    return this.footerContent.bottomLinks.map(link => {
      if (!link.linkHref) {
        return <li>{link.linkText}</li>;
      }
      let linkId = this.uniqueId(link.linkText);

      return (
        <li>
          <a href={link.linkHref} {...(link.openInNewWindow && { target: '_blank' })} title={link.linkText} id={linkId}>
            {link.linkText}
          </a>
        </li>
      );
    });
  }

  renderDefaultFooter() {
    return (
      <Host language={this.language} market={this.market} environment={this.environment}>
        <pn-site-footer url={this.siteDefinition?.url ?? ''}>
          <slot name="logo"></slot>
          {this.renderCols()}

          <div slot="bottom">
            {this.gotData && this.footerContent?.copyrightText ? <span>{this.footerContent.copyrightText}</span> : null}
            {this.gotData && this.footerContent?.bottomLinks ? (
              <span>
                {this.footerContent.bottomLinks.map(link => {
                  if (!link.linkHref) {
                    return <span>{link.linkText}</span>;
                  }
                  let linkId = this.uniqueId(link.linkText);
                  return (
                    <a href={link.linkHref} {...(link.openInNewWindow && { target: '_blank' })} title={link.linkText} id={linkId}>
                      {link.linkText}
                    </a>
                  );
                })}
              </span>
            ) : null}
          </div>
        </pn-site-footer>
      </Host>
    );
  }

  renderSocialMeidaIconListElements() {
    return (
      this.gotData &&
      this.footerContent?.socialmedialinks.map(link => {
        let linkText = getSocialMediaIconsByType(link.linkType, '#00A0D6');
        let linkId = this.uniqueId(link.linkText);
        return (
          <li>
            <a href={link.linkHref} {...(link.openInNewWindow && { target: '_blank' })} title={link.linkText} innerHTML={linkText} id={linkId}></a>
          </li>
        );
      })
    );
  }

  renderSwanComponent() {
    return (
      this.showSwan && (
        <div class="swan-wrapper">
          <div class="separator"></div>
          <pn-swan language={this.language} license-number={this.footerContent?.swanInfo?.swanLicense} link-to={this.footerContent?.swanInfo?.swanLink} />
        </div>
      )
    );
  }

  renderDotComFooter() {
    return (
      <Host language={this.language} market={this.market} environment={this.environment}>
        <pn-site-footer theme={this.theme} url={this.siteDefinition?.url ?? ''}>
          {this.renderCols()}
          <slot name="middle-extra"></slot>
          <slot name="middle-extra-mobile"></slot>
          <div slot="bottom">
            <div class={`social-media ${this.showSwan ? 'social-media--swan' : ''}`}>
              <ul>{this.renderSocialMeidaIconListElements()}</ul>
              {this.renderSwanComponent()}
            </div>

            <div class="copyright">
              {this.gotData && this.footerContent?.copyrightText ? <span>{this.footerContent.copyrightText}</span> : null}
              {this.gotData && this.footerContent?.bottomLinks ? <ul>{this.renderBottomLinks()}</ul> : null}
            </div>
          </div>
        </pn-site-footer>
      </Host>
    );
  }

  render() {
    switch (this.theme) {
      case 'dotcom':
        return this.renderDotComFooter();
      default:
        return this.renderDefaultFooter();
    }
  }
}
